import React, { useCallback, useEffect, useState } from 'react';
import './NotificationManagement.css';
import { useAuth } from '../../contexts/AuthContext';
import { API_CONFIG } from '../../config/apiRouting';

const API_BASE = API_CONFIG.gateway.baseUrl;

interface NotificationTemplate {
  enterpriseId: string;
  templateId: string;
  name: string;
  subject: string;
  bodyHtml?: string | null;
  bodyText?: string | null;
  channels: string[];
  description?: string | null;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface NotificationRecord {
  notificationId: string;
  enterpriseId: string;
  employeeId?: string;
  subject: string;
  bodyText?: string | null;
  bodyHtml?: string | null;
  templateId?: string | null;
  createdAt: string;
  createdBy?: string;
  acknowledgedAt?: string | null;
}

interface VerifiedIdentity {
  identity: string;
  type: 'DOMAIN' | 'EMAIL_ADDRESS' | string;
  verified: boolean;
  sendingEnabled: boolean;
}

interface NotificationManagementProps {
  user: any;
}

const DEFAULT_FORM: Omit<NotificationTemplate, 'enterpriseId' | 'createdAt' | 'updatedAt'> = {
  templateId: '',
  name: '',
  subject: '',
  bodyHtml: '',
  bodyText: '',
  channels: ['email'],
  description: '',
  active: true,
};

const NotificationManagement: React.FC<NotificationManagementProps> = ({ user }) => {
  const { getIdToken } = useAuth();

  const enterpriseId: string =
    user?.enterpriseId ||
    user?.attributes?.['custom:enterpriseId'] ||
    '17527771077049luj0w1ek';

  const [activeTab, setActiveTab] = useState<'recent' | 'templates' | 'identities' | 'test'>('recent');
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [identities, setIdentities] = useState<VerifiedIdentity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [templateForm, setTemplateForm] = useState(DEFAULT_FORM);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

  const [testForm, setTestForm] = useState({
    channel: 'email' as 'email' | 'sms',
    to: '',
    subject: 'BSO test notification',
    bodyText: 'This is a test notification sent from BigSystemOne.',
  });
  const [testResult, setTestResult] = useState<any>(null);

  const [verifyForm, setVerifyForm] = useState({ emailAddress: '' });

  const authHeaders = useCallback(async (): Promise<HeadersInit> => {
    const token = await getIdToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [getIdToken]);

  const loadTemplates = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const r = await fetch(`${API_BASE}/api/notifications/templates?enterpriseId=${encodeURIComponent(enterpriseId)}`, { headers });
      const body = await r.json();
      if (r.ok && body.success) setTemplates(body.data || []);
      else throw new Error(body?.message || `Load templates failed (${r.status})`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load templates');
    }
  }, [authHeaders, enterpriseId]);

  const loadRecent = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const r = await fetch(`${API_BASE}/api/notifications/recent?enterpriseId=${encodeURIComponent(enterpriseId)}&limit=100`, { headers });
      const body = await r.json();
      if (r.ok && body.success) setNotifications(body.data || []);
      else throw new Error(body?.message || `Load recent failed (${r.status})`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load recent notifications');
    }
  }, [authHeaders, enterpriseId]);

  const loadIdentities = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const r = await fetch(`${API_BASE}/api/notifications/verified-emails`, { headers });
      const body = await r.json();
      if (r.ok && body.success) setIdentities(body.data || []);
      else throw new Error(body?.message || `Load identities failed (${r.status})`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load verified identities');
    }
  }, [authHeaders]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      await Promise.all([loadTemplates(), loadRecent(), loadIdentities()]);
      if (mounted) setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [loadTemplates, loadRecent, loadIdentities]);

  const saveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim() || (!templateForm.bodyHtml?.trim() && !templateForm.bodyText?.trim())) {
      setError('Name, subject, and at least one body (HTML or text) are required');
      return;
    }
    setError(null);
    try {
      const headers = await authHeaders();
      const payload: any = {
        enterpriseId,
        name: templateForm.name.trim(),
        subject: templateForm.subject.trim(),
        bodyHtml: templateForm.bodyHtml || null,
        bodyText: templateForm.bodyText || null,
        channels: templateForm.channels,
        description: templateForm.description || null,
        active: templateForm.active,
      };
      if (editingTemplateId) payload.templateId = editingTemplateId;
      const r = await fetch(`${API_BASE}/api/notifications/templates`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const body = await r.json();
      if (!r.ok || !body.success) throw new Error(body?.message || `Save failed (${r.status})`);
      setMessage(editingTemplateId ? 'Template updated' : 'Template created');
      setTemplateForm(DEFAULT_FORM);
      setEditingTemplateId(null);
      await loadTemplates();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const editTemplate = (t: NotificationTemplate) => {
    setEditingTemplateId(t.templateId);
    setTemplateForm({
      templateId: t.templateId,
      name: t.name,
      subject: t.subject,
      bodyHtml: t.bodyHtml || '',
      bodyText: t.bodyText || '',
      channels: t.channels || ['email'],
      description: t.description || '',
      active: t.active !== false,
    });
    setActiveTab('templates');
  };

  const deleteTemplate = async (t: NotificationTemplate) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      const headers = await authHeaders();
      const r = await fetch(
        `${API_BASE}/api/notifications/templates/${encodeURIComponent(t.templateId)}?enterpriseId=${encodeURIComponent(enterpriseId)}`,
        { method: 'DELETE', headers }
      );
      const body = await r.json();
      if (!r.ok || !body.success) throw new Error(body?.message || `Delete failed (${r.status})`);
      setMessage('Template deleted');
      await loadTemplates();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const sendTest = async () => {
    if (!testForm.to) {
      setError('Recipient required');
      return;
    }
    setError(null);
    setTestResult(null);
    try {
      const headers = await authHeaders();
      const r = await fetch(`${API_BASE}/api/notifications/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          channel: testForm.channel,
          to: testForm.to,
          subject: testForm.subject,
          bodyText: testForm.bodyText,
        }),
      });
      const body = await r.json();
      setTestResult(body);
      if (r.ok && body.success) {
        setMessage(`${testForm.channel.toUpperCase()} sent`);
        await loadRecent();
      } else {
        setError(body?.message || `Test send failed (${r.status})`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test send failed');
    }
  };

  const verifyEmail = async () => {
    if (!verifyForm.emailAddress.trim()) {
      setError('Email address required');
      return;
    }
    setError(null);
    try {
      const headers = await authHeaders();
      const r = await fetch(`${API_BASE}/api/notifications/verify-email`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ emailAddress: verifyForm.emailAddress.trim() }),
      });
      const body = await r.json();
      if (!r.ok || !body.success) throw new Error(body?.message || `Verify failed (${r.status})`);
      setMessage(body.data?.note || 'Verification requested');
      setVerifyForm({ emailAddress: '' });
      await loadIdentities();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verify failed');
    }
  };

  const toggleChannel = (ch: string) => {
    setTemplateForm((f) => {
      const set = new Set(f.channels);
      set.has(ch) ? set.delete(ch) : set.add(ch);
      return { ...f, channels: Array.from(set) };
    });
  };

  if (loading && templates.length === 0 && notifications.length === 0) {
    return (
      <div className="notification-management">
        <p>Loading notifications…</p>
      </div>
    );
  }

  return (
    <div className="notification-management">
      <div className="notification-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h1>Notifications</h1>
        <div style={{ color: '#666', fontSize: '0.9rem' }}>Enterprise: {enterpriseId}</div>
      </div>

      <div className="notification-tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {(['recent', 'templates', 'identities', 'test'] as const).map((t) => (
          <button
            key={t}
            onClick={() => { setActiveTab(t); setError(null); setMessage(null); }}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ccc',
              background: activeTab === t ? '#0369a1' : 'white',
              color: activeTab === t ? 'white' : '#111',
              borderRadius: 4,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {t === 'identities' ? 'Verified Identities' : t}
          </button>
        ))}
      </div>

      {error && (
        <div role="alert" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: '#fee', color: '#900', borderRadius: 4 }}>
          {error}
        </div>
      )}
      {message && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: '#dfd', color: '#060', borderRadius: 4 }}>
          {message}
        </div>
      )}

      {activeTab === 'recent' && (
        <div>
          <h2>Recent Notifications</h2>
          {notifications.length === 0 ? (
            <p style={{ color: '#666' }}>No notifications sent yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>When</th>
                  <th style={{ padding: '0.5rem' }}>Subject</th>
                  <th style={{ padding: '0.5rem' }}>Employee</th>
                  <th style={{ padding: '0.5rem' }}>Template</th>
                  <th style={{ padding: '0.5rem' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((n) => (
                  <tr key={n.notificationId} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.5rem' }}>{new Date(n.createdAt).toLocaleString()}</td>
                    <td style={{ padding: '0.5rem' }}>{n.subject}</td>
                    <td style={{ padding: '0.5rem' }}>{n.employeeId || '—'}</td>
                    <td style={{ padding: '0.5rem' }}>{n.templateId || '—'}</td>
                    <td style={{ padding: '0.5rem' }}>{n.acknowledgedAt ? 'Read' : 'Unread'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'templates' && (
        <div>
          <h2>{editingTemplateId ? 'Edit Template' : 'New Template'}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <label>
              Name*
              <input
                type="text"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </label>
            <label>
              Subject*
              <input
                type="text"
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </label>
          </div>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            HTML Body
            <textarea
              value={templateForm.bodyHtml || ''}
              onChange={(e) => setTemplateForm({ ...templateForm, bodyHtml: e.target.value })}
              rows={6}
              style={{ width: '100%', padding: '0.5rem', fontFamily: 'monospace' }}
              placeholder="<p>Hello {{name}}</p>"
            />
          </label>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            Text Body
            <textarea
              value={templateForm.bodyText || ''}
              onChange={(e) => setTemplateForm({ ...templateForm, bodyText: e.target.value })}
              rows={4}
              style={{ width: '100%', padding: '0.5rem' }}
              placeholder="Hello {{name}}"
            />
          </label>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
            <span>Channels:</span>
            {['email', 'sms', 'inbox'].map((ch) => (
              <label key={ch} style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={templateForm.channels.includes(ch)}
                  onChange={() => toggleChannel(ch)}
                />{' '}
                {ch}
              </label>
            ))}
            <label style={{ marginLeft: 'auto', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={templateForm.active}
                onChange={(e) => setTemplateForm({ ...templateForm, active: e.target.checked })}
              />{' '}
              Active
            </label>
          </div>
          <label style={{ display: 'block', marginBottom: '1rem' }}>
            Description
            <input
              type="text"
              value={templateForm.description || ''}
              onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
              style={{ width: '100%', padding: '0.5rem' }}
            />
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={saveTemplate} style={{ padding: '0.5rem 1rem', background: '#0369a1', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              {editingTemplateId ? 'Save changes' : 'Create template'}
            </button>
            {editingTemplateId && (
              <button onClick={() => { setEditingTemplateId(null); setTemplateForm(DEFAULT_FORM); }} style={{ padding: '0.5rem 1rem' }}>
                Cancel
              </button>
            )}
          </div>

          <h3 style={{ marginTop: '2rem' }}>Existing Templates ({templates.length})</h3>
          {templates.length === 0 ? (
            <p style={{ color: '#666' }}>No templates yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>Name</th>
                  <th style={{ padding: '0.5rem' }}>Subject</th>
                  <th style={{ padding: '0.5rem' }}>Channels</th>
                  <th style={{ padding: '0.5rem' }}>Active</th>
                  <th style={{ padding: '0.5rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.templateId} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.5rem' }}>{t.name}</td>
                    <td style={{ padding: '0.5rem' }}>{t.subject}</td>
                    <td style={{ padding: '0.5rem' }}>{(t.channels || []).join(', ')}</td>
                    <td style={{ padding: '0.5rem' }}>{t.active ? 'Yes' : 'No'}</td>
                    <td style={{ padding: '0.5rem' }}>
                      <button onClick={() => editTemplate(t)} style={{ marginRight: '0.25rem' }}>Edit</button>
                      <button onClick={() => deleteTemplate(t)} style={{ color: '#900' }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'identities' && (
        <div>
          <h2>Verified SES Identities</h2>
          <p style={{ color: '#666' }}>
            SES is in sandbox mode for this account. Test sends will only deliver to verified addresses below
            or to addresses in a verified domain (e.g., bigsystemone.com).
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <input
              type="email"
              placeholder="address@example.com"
              value={verifyForm.emailAddress}
              onChange={(e) => setVerifyForm({ emailAddress: e.target.value })}
              style={{ flex: 1, padding: '0.5rem' }}
            />
            <button onClick={verifyEmail} style={{ padding: '0.5rem 1rem', background: '#0369a1', color: 'white', border: 'none', borderRadius: 4 }}>
              Request verification
            </button>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem' }}>Identity</th>
                <th style={{ padding: '0.5rem' }}>Type</th>
                <th style={{ padding: '0.5rem' }}>Verified</th>
                <th style={{ padding: '0.5rem' }}>Sending</th>
              </tr>
            </thead>
            <tbody>
              {identities.map((i) => (
                <tr key={i.identity} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>{i.identity}</td>
                  <td style={{ padding: '0.5rem' }}>{i.type}</td>
                  <td style={{ padding: '0.5rem' }}>{i.verified ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.5rem' }}>{i.sendingEnabled ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'test' && (
        <div>
          <h2>Send Test Notification</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '0.75rem', maxWidth: 720 }}>
            <label>Channel</label>
            <select
              value={testForm.channel}
              onChange={(e) => setTestForm({ ...testForm, channel: e.target.value as 'email' | 'sms' })}
              style={{ padding: '0.5rem' }}
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>

            <label>Recipient</label>
            <input
              type={testForm.channel === 'email' ? 'email' : 'tel'}
              placeholder={testForm.channel === 'email' ? 'person@example.com' : '+15555551234'}
              value={testForm.to}
              onChange={(e) => setTestForm({ ...testForm, to: e.target.value })}
              style={{ padding: '0.5rem' }}
            />

            {testForm.channel === 'email' && (
              <>
                <label>Subject</label>
                <input
                  type="text"
                  value={testForm.subject}
                  onChange={(e) => setTestForm({ ...testForm, subject: e.target.value })}
                  style={{ padding: '0.5rem' }}
                />
              </>
            )}

            <label>Body</label>
            <textarea
              rows={4}
              value={testForm.bodyText}
              onChange={(e) => setTestForm({ ...testForm, bodyText: e.target.value })}
              style={{ padding: '0.5rem' }}
            />
          </div>

          <button onClick={sendTest} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#0369a1', color: 'white', border: 'none', borderRadius: 4 }}>
            Send
          </button>

          {testResult && (
            <pre style={{ marginTop: '1rem', padding: '0.75rem', background: '#f5f5f5', borderRadius: 4, overflow: 'auto' }}>
              {JSON.stringify(testResult, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationManagement;
